// The result side of a calculation form: the shown run (status, progress, download, plots) and the project's runs.
import { Button, Card, Group, Stack, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { ApiError, download, formatDate, get, type Job, type ProjectDetail } from "../lib/api";
import type { LimitInfo } from "../lib/transport";
import { ErrorBox, notifyError } from "./Errors";
import { CancelButton, JobProgress, JobStatusBadge } from "./Jobs";

/** A plan limit reported by a form check, shown like a refused request (with a "Request access" button). */
export function limitError(l: LimitInfo) {
  return new ApiError(403, l);
}

/** The project's latest usable run of ``kind`` (not failed or cancelled). */
export function latestRun(project: ProjectDetail, kind: string) {
  return project.jobs.find((j) => j.kind === kind && j.status !== "failed" && j.status !== "cancelled");
}

export function RunCard({ job, empty, exportName, extra, children }: {
  job: Job | undefined; empty: ReactNode; exportName?: string; extra?: ReactNode; children?: ReactNode;
}) {
  return (
    <Card withBorder mih={300}>
      {!job ? empty : (
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap="xs">
              <JobStatusBadge status={job.status} />
              <Text size="sm" c="dimmed">{formatDate(job.created_at, true)}</Text>
            </Group>
            <Group gap="xs">
              <CancelButton job={job} />
              {extra}
              {job.status === "succeeded" && job.result?.export_file && exportName && (
                <Button size="xs" variant="light"
                  onClick={() => download(`/files/${job.result!.export_file}/download`, exportName).catch(notifyError)}>
                  Download data & figures (.zip)
                </Button>
              )}
            </Group>
          </Group>
          <JobProgress job={job} />
          {job.error && <ErrorBox error={new Error(job.error)} />}
          {children}
        </Stack>
      )}
    </Card>
  );
}

export function PreviousRuns({ project, kind, current, onOpen, describe }: {
  project: ProjectDetail; kind: string; current?: string; onOpen: (id: string) => void;
  describe: (params: Record<string, any>) => string;
}) {
  const runs = project.jobs.filter((j) => j.kind === kind).slice(0, 10);
  const details = useQuery({
    queryKey: ["transport-runs", project.id, runs.map((j) => j.id + j.status).join()],
    queryFn: () => Promise.all(runs.map((j) => get<Job>(`/jobs/${j.id}`))),
    enabled: runs.length > 0,
  });
  if (!runs.length) return null;
  return (
    <Card withBorder mt="md">
      <Text fw={600} size="sm" mb="xs">Runs in this project</Text>
      <Stack gap={4}>
        {(details.data ?? runs).map((j) => (
          <Group key={j.id} justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              <JobStatusBadge status={j.status} />
              <Text size="sm">{describe((j.params ?? {}) as Record<string, any>)}</Text>
            </Group>
            <Group gap="xs" wrap="nowrap">
              <Text size="xs" c="dimmed">{formatDate(j.created_at, true)}</Text>
              <Button size="compact-xs" variant={j.id === current ? "light" : "subtle"} onClick={() => onOpen(j.id)}>
                {j.id === current ? "Shown" : "Show"}
              </Button>
            </Group>
          </Group>
        ))}
      </Stack>
    </Card>
  );
}
